import { Autocomplete, CircularProgress, TextField, Typography, Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import ApiService from "@/api/ApiService";
import { usePersistantStore } from "@/stores/global";
import { OrderCategory, OrderSearchContext } from "@/shared/orders/resourceOrderSearch";

const categoryLabel = (tag: OrderCategory) => [tag.superCategory, tag.name].filter(Boolean).join(" → ");

export default function ResourceCategorySearch({ context, region, type, currentId, onSelect, onCurrentTag }: {
  context: OrderSearchContext;
  region: number;
  type: OrderCategory["type"];
  currentId: number | null;
  onSelect: (tag: OrderCategory | null) => void;
  onCurrentTag: (tag: OrderCategory | null) => void;
}) {
  const [text, setText] = useState("");
  const [queryText, setQueryText] = useState("");
  const [selected, setSelected] = useState<OrderCategory | null>(null);
  const userId = usePersistantStore(s => s.userId);
  useEffect(() => {
    const timeout = setTimeout(() => setQueryText(text.trim()), 300);
    return () => clearTimeout(timeout);
  }, [text]);
  useEffect(() => { setText(""); setQueryText(""); }, [context.type, context.id, region, userId, type]);
  const query = useQuery({
    queryKey: ["resourceOrderCategories", "client", userId, context.type, context.id, region, type, queryText, currentId],
    queryFn: ApiService.searchResourceOrderCategories(context, queryText, currentId ?? undefined, type),
    enabled: context.id > 0,
  });
  useEffect(() => {
    if (query.isSuccess) {
      setSelected(previous => previous?.id === query.data.selectedTag?.id ? previous : query.data.selectedTag);
      onCurrentTag(query.data.selectedTag);
    }
  }, [query.isSuccess, query.data, onCurrentTag]);
  const loading = text.trim() !== queryText || query.isFetching || query.isPending;
  return <Autocomplete<OrderCategory>
    options={loading ? [] : query.data?.tags ?? []}
    filterOptions={options => options}
    getOptionLabel={categoryLabel}
    isOptionEqualToValue={(a, b) => a.id === b.id}
    value={selected?.id === currentId && selected.type === type ? selected : null}
    onInputChange={(_, value, reason) => { if (reason === "input") setText(value); }}
    onChange={(_, tag) => { setSelected(tag); onSelect(tag); setText(""); setQueryText(""); }}
    clearText="Auswahl löschen"
    openText="Auswahl öffnen"
    closeText="Auswahl schließen"
    loading={loading && context.id > 0}
    loadingText="Kategorien werden gesucht…"
    noOptionsText={query.isError ? "Kategorien konnten nicht geladen werden. Bitte erneut suchen." : "Keine passende Kategorie gefunden"}
    renderOption={(props, tag) => {
      const { key: _key, ...optionProps } = props;
      return <Box component="li" key={`${tag.id}:${_key}`} {...optionProps}><Typography>{categoryLabel(tag)}</Typography></Box>;
    }}
    renderInput={params => <TextField {...params} label="Kategorie, Ressourcenname oder Nummer" error={query.isError}
      inputProps={{ ...params.inputProps, maxLength: 120 }}
      helperText={context.id <= 0 ? "Bitte zuerst das Projekt speichern." : query.data?.hasMore ? "Weitere Kategorien vorhanden. Bitte die Suche verfeinern." : "Der Treffer wählt die Kategorie; das Wunschgerät wählen Sie anschließend separat."}
      InputProps={{ ...params.InputProps, endAdornment: <>{loading && context.id > 0 ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</> }}
    />}
  />;
}
